<!-- eslint-disable vue/max-attributes-per-line -->
<!--
  新クロージングページ（日報β専用）。
  ステップ: データセット管理 → 経費管理 → ユーザー集計 → 卓集計 → 店舗集計 → 完了。
  ユーザー集計／卓集計／店舗集計は、対応する集計表が 0 件のときステッパーから
  除外する（データセット管理・経費管理・完了は常に表示）。
  既存 eod-closing は温存し、本ページは別ルート（eod-closing-v2）として新規追加している。
  日計（ユーザー/卓/店舗）はスプレッドシート形式で、集計取得・保存・再集計は
  既存 eod-closing のAPI（useEodClosingV2Aggregations 経由）を流用する。
-->
<template>
  <v-container :key="`eod-closing-v2-${date}`">
    <h1 class="mb-4">
      {{ date }} クロージング
    </h1>

    <!-- 日付選択 -->
    <div class="d-flex align-center flex-wrap mb-12">
      <v-menu
        transition="scale-transition"
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="date"
            :prepend-icon="icons.mdiCalendar"
            readonly
            hide-details
            style="max-width: 200px;"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="date"
          locale="ja"
          :day-format="d => new Date(d).getDate()"
          :max="isProduction ? today : undefined"
          color="primary"
          no-title
        />
      </v-menu>

      <v-btn
        color="secondary"
        small
        class="ml-3"
        :ripple="false"
        @click="date = today"
      >
        本日
      </v-btn>

      <v-btn
        color="info"
        small
        class="ml-2"
        :ripple="false"
        :to="{ name: 'daily-report-summary', query: { date } }"
      >
        日報
      </v-btn>
    </div>

    <!-- 天気・イベント内容・日報記入者（全ステップ共通で常時表示） -->
    <div class="record-section mb-4">
      <v-row>
        <v-col
          cols="12"
          md="4"
        >
          <daily-weather
            :key="`weather-${date}`"
            :date="date"
            :readonly="false"
          />
        </v-col>
        <v-col
          cols="12"
          md="8"
        >
          <daily-club-event
            :key="`event-${date}`"
            :date="date"
            :readonly="false"
          />
        </v-col>
      </v-row>
      <daily-report-closer
        :key="`closer-${date}`"
        :date="date"
        :readonly="false"
        class="mt-4"
      />
    </div>

    <!-- ステッパー -->
    <v-stepper
      v-model="step"
      flat
      class="eod-closing-v2-stepper mb-4"
    >
      <v-stepper-header>
        <template v-for="(stepDef, idx) in steps">
          <v-stepper-step
            :key="`step-${stepDef.key}`"
            :complete="step > idx"
            editable
            :step="idx"
          >
            {{ stepDef.label }}
            <!-- カテゴリ（ユーザー/卓/店舗集計）の集計状態マーク -->
            <v-icon
              v-if="stepMarkMeta(stepDef.key)"
              :color="stepMarkMeta(stepDef.key).color"
              size="30"
              class="eod-v2-step-mark"
            >
              {{ stepMarkMeta(stepDef.key).icon }}
            </v-icon>
          </v-stepper-step>
          <v-divider
            v-if="idx < steps.length - 1"
            :key="`step-divider-${stepDef.key}`"
          />
        </template>
      </v-stepper-header>

      <v-stepper-items>
        <v-stepper-content
          v-for="(stepDef, idx) in steps"
          :key="`step-content-${stepDef.key}`"
          :step="idx"
          eager
        >
          <h2
            v-if="!['user', 'table', 'club'].includes(stepDef.key)"
            class="mb-3"
          >
            {{ stepDef.label }}
          </h2>

          <!-- データセット管理 -->
          <template v-if="stepDef.key === 'dataset'">
            <timecards-table-inline :date="date" class="mb-6" />
            <data-tabs :show-date-picker="false" />
          </template>

          <!-- 経費管理（諸経費 / 諸入費をタブで切り替え） -->
          <template v-else-if="stepDef.key === 'expense'">
            <v-tabs
              v-model="expenseTabIndex"
              background-color="transparent"
            >
              <v-tab>諸経費</v-tab>
              <v-tab>諸入費</v-tab>
            </v-tabs>
            <v-tabs-items v-model="expenseTabIndex">
              <v-tab-item>
                <expenses-table :date="date" />
              </v-tab-item>
              <v-tab-item>
                <benefits-table
                  :key="`benefits-table-${date}`"
                  :date="date"
                />
              </v-tab-item>
            </v-tabs-items>
          </template>

          <!-- ユーザー集計 -->
          <daily-aggregation-step
            v-else-if="stepDef.key === 'user'"
            category-label="ユーザー集計"
            :date="date"
            :aggregations-data="userAggregationsData"
            :aggregations-map="userAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            :available-users="availableUsers"
            @save="onSave"
            @save-cell="generateBusinessRuleResult"
            @reload="reloadAggregation"
            @refresh="refreshAggregation"
            @bulk-enqueue="onBulkEnqueueUser"
            @fetch-available-users="onFetchAvailableUsers"
            @create-user="onCreateUser"
            @destroy-user="onDestroyUser"
          />

          <!-- 卓集計 -->
          <daily-aggregation-step
            v-else-if="stepDef.key === 'table'"
            category-label="卓集計"
            :date="date"
            :aggregations-data="tableAggregationsData"
            :aggregations-map="tableAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            @save="onSave"
            @save-cell="generateBusinessRuleResult"
            @reload="reloadAggregation"
            @refresh="refreshAggregation"
            @bulk-enqueue="onBulkEnqueueTable"
          />

          <!-- 店舗集計 -->
          <daily-aggregation-step
            v-else-if="stepDef.key === 'club'"
            category-label="店舗集計"
            :date="date"
            :aggregations-data="clubAggregationsData"
            :aggregations-map="clubAggregationsMap"
            :job-statuses="jobStatuses"
            :job-error-messages="jobErrorMessages"
            :job-timestamps="jobTimestamps"
            :loading-statuses="loadingStatuses"
            :is-loading="isLoading"
            :status-resolver="sheetStatus"
            @save="onSave"
            @save-cell="generateBusinessRuleResult"
            @reload="reloadAggregation"
            @refresh="refreshAggregation"
            @bulk-enqueue="onBulkEnqueueClub"
          />

          <!-- 完了 -->
          <eod-closing-complete-step
            v-else-if="stepDef.key === 'done'"
            :date="date"
            :completed="allClosingCompleted"
          />
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>
  </v-container>
</template>

<script>
import {
  ref, inject, computed, watch, onMounted, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiCalendar, mdiAlert, mdiCheckBold, mdiExclamationThick,
} from '@mdi/js'
import { parseISO, isAfter } from 'date-fns'
import { useRouter } from '@core/utils'
import { useProcessEnv } from '@/views/v2-temp/common/composables/useProcessEnv'
import { handleApiError } from '@/utils/errorHandler'
import TimecardsTableInline from './components/TimecardsTableInline.vue'
import ExpensesTable from '@/views/v2-temp/eod-closing/components/ExpensesTable.vue'
import DataTabs from '@/views/waiter/Data.vue'
import DailyAggregationStep from './components/DailyAggregationStep.vue'
import EodClosingCompleteStep from './components/EodClosingCompleteStep.vue'
import DailyWeather from '@/views/components/daily_report/Closing/DailyWeather.vue'
import DailyClubEvent from '@/views/components/daily_report/Closing/DailyClubEvent.vue'
import DailyReportCloser from '@/views/components/daily_report/Closing/DailyReportCloser.vue'
import BenefitsTable from './components/BenefitsTable.vue'
import useEodClosingV2Aggregations from './composables/useEodClosingV2Aggregations'

// ステップ（カテゴリ）状態マークの表示メタ。マークアイコンのみ表示する。
const STEP_MARK_META = {
  uncounted: { color: 'error', icon: mdiExclamationThick },
  unsaved: { color: 'warning', icon: mdiAlert },
  done: { color: 'success', icon: mdiCheckBold },
}

export default {
  name: 'EodClosingV2',
  components: {
    TimecardsTableInline,
    ExpensesTable,
    DataTabs,
    DailyAggregationStep,
    EodClosingCompleteStep,
    DailyWeather,
    DailyClubEvent,
    DailyReportCloser,
    BenefitsTable,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { route, router } = useRouter()
    const { isProduction } = useProcessEnv()
    const currentClubMeta = inject('currentClubMeta', { today: null })
    const today = currentClubMeta.today || new Date().toISOString().slice(0, 10)
    const date = ref(route.value.query.date || today)
    const step = ref(0)

    // 経費管理ステップの「諸経費 / 諸入費」タブの選択状態（0: 諸経費, 1: 諸入費）。
    const expenseTabIndex = ref(0)

    // APIエラーハンドラ（既存 eod-closing と同じ handleApiError パターン）
    const onApiError = error => handleApiError(error, { $toast: vm.$toast })

    const {
      isLoading,
      loadingStatuses,
      jobTimestamps,
      jobErrorMessages,
      jobStatuses,
      availableUsers,
      userAggregationsData,
      tableAggregationsData,
      clubAggregationsData,
      userAggregationsMap,
      tableAggregationsMap,
      clubAggregationsMap,
      sheetStatus,
      getAggregations,
      reloadAggregation,
      refreshAggregation,
      enqueueAggregationsByType,
      enqueueGenerateBusinessRuleResults,
      generateBusinessRuleResult,
      fetchAvailableUsers,
      createUserAggregation,
      destroyUserAggregation,
    } = useEodClosingV2Aggregations(date, { handleApiError: onApiError })

    // ステッパー構成（動的）。
    // データセット管理・経費管理・完了は常に表示。
    // ユーザー集計／卓集計／店舗集計は、対応する集計表が 0 件のとき除外する。
    // ただし読み込み中（isLoading）は誤って消さないよう、ロード完了までは
    // 3 カテゴリとも表示しておく（aggregationsData が出揃ってから判定）。
    const steps = computed(() => {
      const list = [
        { key: 'dataset', label: 'データセット管理' },
        { key: 'expense', label: '経費管理' },
      ]

      if (isLoading.value || userAggregationsData.value.length > 0) {
        list.push({ key: 'user', label: 'ユーザー集計' })
      }
      if (isLoading.value || tableAggregationsData.value.length > 0) {
        list.push({ key: 'table', label: '卓集計' })
      }
      if (isLoading.value || clubAggregationsData.value.length > 0) {
        list.push({ key: 'club', label: '店舗集計' })
      }

      list.push({ key: 'done', label: '完了' })

      return list
    })

    // --- ステップ（カテゴリ）単位の集計状態マーク ----------------------
    // カテゴリ内の集計表の状態を集約し、未集計 > 未保存 > 完了 の優先で
    // 1 つの状態を返す（未集計が最も手前の状態）。
    //   - 'uncounted' : 集計ジョブ未実行の集計表が 1 つでもある
    //   - 'unsaved'   : 未集計は無いが、未保存・失敗・集計中の集計表がある
    //   - 'done'      : すべて保存済み
    //   - null        : 集計表が無い（マーク非表示）
    const resolveCategoryStatus = list => {
      if (!list || list.length === 0) return null
      let hasUncounted = false
      let hasIncomplete = false
      list.forEach(agg => {
        const { id } = agg.attributes
        const jobStatus = jobStatuses.value[id]
        if (jobStatus === null || jobStatus === undefined) {
          hasUncounted = true

          return
        }
        if (sheetStatus(id) !== 'saved') hasIncomplete = true
      })
      if (hasUncounted) return 'uncounted'
      if (hasIncomplete) return 'unsaved'

      return 'done'
    }

    const userStepStatus = computed(() => resolveCategoryStatus(userAggregationsData.value))
    const tableStepStatus = computed(() => resolveCategoryStatus(tableAggregationsData.value))
    const clubStepStatus = computed(() => resolveCategoryStatus(clubAggregationsData.value))

    // ステップ key -> 状態マークの表示メタ（対象外ステップ・集計表なしは null）。
    const stepMarkMeta = key => {
      const status = {
        user: userStepStatus.value,
        table: tableStepStatus.value,
        club: clubStepStatus.value,
      }[key]

      return status ? STEP_MARK_META[status] : null
    }

    // 全カテゴリのクロージングが完了しているか（未集計・未保存が無い）。
    // 集計表が無いカテゴリ（null）は「やることなし」として完了扱い。
    const allClosingCompleted = computed(() => (
      [userStepStatus.value, tableStepStatus.value, clubStepStatus.value]
        .every(s => s === null || s === 'done')
    ))

    // ステップ数が動的に変わるため、現在ステップが範囲外になったら丸める。
    watch(steps, newSteps => {
      const maxIndex = newSteps.length - 1
      if (step.value > maxIndex) step.value = maxIndex
      if (step.value < 0) step.value = 0
    })

    // シートの「保存」操作: 一括保存APIを呼ぶ
    const onSave = id => {
      enqueueGenerateBusinessRuleResults(id)
    }

    // 日計ジョブ一括実行（カテゴリ単位）。
    // 子（DailyAggregationStep）から渡される done を、enqueue 完了後に呼んで
    // ボタンの loading 状態を解除させる。
    const bulkEnqueue = async (type, done) => {
      try {
        await enqueueAggregationsByType(type)
      } finally {
        if (typeof done === 'function') done()
      }
    }
    const onBulkEnqueueUser = ({ done }) => bulkEnqueue('EodClosingUserAggregation', done)
    const onBulkEnqueueTable = ({ done }) => bulkEnqueue('EodClosingTableAggregation', done)
    const onBulkEnqueueClub = ({ done }) => bulkEnqueue('EodClosingClubAggregation', done)

    // 適用ユーザーの変更（ユーザー集計ステップから）
    const onFetchAvailableUsers = ({ id, callback }) => {
      fetchAvailableUsers(id, callback)
    }
    const onCreateUser = ({ id, userId, callback }) => {
      createUserAggregation(id, { userId, callback })
    }
    const onDestroyUser = ({
      aggregationId, id, userId, callback,
    }) => {
      destroyUserAggregation(aggregationId, { id, userId, callback })
    }

    // 日付変更時: 本番では未来日を弾き、URLクエリを同期し、集計を取り直す
    watch(date, newDate => {
      const dateObj = parseISO(newDate)
      const todayObj = parseISO(today)
      if (isProduction && isAfter(dateObj, todayObj)) {
        date.value = today

        return
      }

      if (route.value.query.date !== date.value) {
        router.replace({
          name: route.value.name,
          query: { ...route.value.query, date: date.value },
        })
      }
      getAggregations()
    })

    onMounted(() => {
      getAggregations()
    })

    return {
      date,
      today,
      step,
      expenseTabIndex,
      steps,
      stepMarkMeta,
      allClosingCompleted,
      isProduction,

      isLoading,
      loadingStatuses,
      jobTimestamps,
      jobErrorMessages,
      jobStatuses,
      availableUsers,
      userAggregationsData,
      tableAggregationsData,
      clubAggregationsData,
      userAggregationsMap,
      tableAggregationsMap,
      clubAggregationsMap,
      sheetStatus,
      reloadAggregation,
      refreshAggregation,
      generateBusinessRuleResult,
      onSave,
      onBulkEnqueueUser,
      onBulkEnqueueTable,
      onBulkEnqueueClub,
      onFetchAvailableUsers,
      onCreateUser,
      onDestroyUser,

      icons: {
        mdiCalendar,
      },
    }
  },
}
</script>

<style lang="scss">
.eod-closing-v2-stepper {
  background: none !important;

  // マークがステップ上端からはみ出す分、ステッパー全体に上余白を確保する
  margin-top: 64px;

  // 既定の overflow: hidden を解除し、上にはみ出すマークが切れないようにする
  overflow: visible !important;

  // マークをラベルの上に絶対配置するための基準
  .v-stepper__label {
    position: relative;
  }

  // ステッパー行と下のステップ内容の間隔を広げる
  .v-stepper__items {
    margin-top: 16px;
  }
}

// ステップ（カテゴリ）の集計状態マーク。ステップラベルの中央の真上に浮かせる。
// v-icon のデフォルト position: relative を打ち消すため !important で absolute を効かせる。
.eod-v2-step-mark {
  position: absolute !important;
  top: -56px;
  left: 50%;
  transform: translateX(-50%);
}
</style>
